
import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';
import ScrollReveal from '../ui/ScrollReveal';
import TypingAnimation from '../ui/TypingAnimation';
import Spline from '@splinetool/react-spline';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [splineLoaded, setSplineLoaded] = useState(false);
  
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const onSplineLoad = () => {
    setSplineLoaded(true);
  };
  
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Spline 3D Background */}
      <div className="absolute inset-0 z-0">
        <Spline
          scene="https://prod.spline.design/RXQAkLvPOo1TVFa9/scene.splinecode"
          onLoad={onSplineLoad}
          className="w-full h-full"
        />
        {/* Overlay to ensure text readability */}
        <div className="absolute inset-0 z-10 bg-zinc-950/70"></div>
      </div>

      {/* Fallback background image if Spline fails to load */}
      {!splineLoaded && (
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 z-10 bg-zinc-950"></div>
          <img src="/lovable-uploads/5a49f210-0474-4f9f-8c57-f3c8066ff074.png" alt="Creative design background" className="w-full h-full object-cover" />
        </div>
      )}

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-20 pt-20 pb-12 md:pb-24">
        <div className="max-w-4xl">
          <ScrollReveal delay={100} className="mb-4">
            <span className="inline-block px-4 py-1.5 bg-white/10 backdrop-blur-sm text-white rounded-full text-sm font-medium border border-white/20">
              Detail-oriented creative partner
            </span>
          </ScrollReveal>

          <h1 className={`text-4xl md:text-6xl lg:text-7xl font-display font-medium text-white mb-6 animated-text ${isLoaded ? 'visible' : 'invisible'}`}>
            <TypingAnimation
            phrases={[
              'Virtual Tour Production',
              'SEO',
              'Drone Work', 
              'Photography',
              'Website design',
              'Video',
              'Custom software',
              'Ad Management'
            ]}
              staticText="That Elevates Your Brand"
              className="leading-tight"
            />
          </h1>

          <ScrollReveal delay={400} className="mb-8">
            <p className="text-xl text-white/90 max-w-xl">
              A detail-oriented creative partner for businesses, organizations and individuals looking to elevate their digital presence.
            </p>
          </ScrollReveal>

          <div className="flex flex-col md:flex-row md:items-center gap-8 mb-12">
            <ScrollReveal delay={600}>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full">
                  <Link to="/contact">
                    Start Your Project
                    <ChevronRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="bg-black/30 backdrop-blur-md border-white border-2 text-white hover:bg-astrem-black hover:border-astrem-black hover:text-white rounded-full transition-all duration-300">
                  <Link to="/portfolio">
                    View Our Work
                  </Link>
                </Button>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={700}>
              <div className="flex items-center mt-8 md:mt-0">
                <div className="flex -space-x-4 mr-4">
                  <div className="w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] lg:w-20 lg:h-20 rounded-full border-2 border-white overflow-hidden relative z-30">
                    <img 
                      src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/evan_profile.jpg" 
                      alt="Evan profile" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] lg:w-20 lg:h-20 rounded-full border-2 border-white overflow-hidden relative z-20">
                    <img 
                      src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Hand_holding_a_business_card_1.jpg" 
                      alt="Business card" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="w-16 h-16 md:w-[4.5rem] md:h-[4.5rem] lg:w-20 lg:h-20 rounded-full border-2 border-white overflow-hidden relative z-10">
                    <img 
                      src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Man_smiling_on_street_bench.jpg" 
                      alt="Smiling man" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="text-white">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-base font-medium">5.0 - Based on 54 Google reviews</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </div>

      {/* Scroll down indicator */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-20 hidden md:block">
        <div className="flex flex-col items-center text-white">
          <span className="text-sm mb-2">Scroll Down</span>
          <div className="w-px h-16 bg-gradient-to-b from-white to-transparent"></div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
